import { SessionConnectionStatus } from '@/services/interfaces/Session';
import { apiManager } from '@/services/implementations/APIManager';
import { AbstractSessionService } from './AbstractSessionService';

// Base64编码的一个极短的静音WAV文件
const SILENT_AUDIO = 'data:audio/wav;base64,UklGRjIAAABXQVZFZm10IBIAAAABAAEARKwAAIhYAQACABAAZGF0YQAAAAA=';

/**
 * Azure OpenAI Realtime API implementation using WebRTC
 */
export class AzureWebRTCSessionService extends AbstractSessionService {
  private peerConnection: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  // 明确命名为 webrtcAudioElement，专门用于播放WebRTC流
  private webrtcAudioElement: HTMLAudioElement | null = null;
  private isAudioContextUnlocked: boolean = false;
  // 用于跟踪解锁操作是否正在进行，防止重复触发
  private isUnlocking: boolean = false;

  constructor() {
    super('AzureWebRTCSessionService');
  }

  /**
   * Service-specific initialization for WebRTC
   */
  protected async initializeService(): Promise<void> {
    this.createWebRTCAudioElement();
  }

  /**
   * 创建用于播放WebRTC音频流的专用元素 (已修复构建错误)
   * @private
   */
  private createWebRTCAudioElement(): void {
    if (!this.webrtcAudioElement) {
      console.log('创建 WebRTC 专用的音频元素');
      this.webrtcAudioElement = document.createElement('audio');

      // 这些属性对于防止在iOS上全屏播放很重要
      this.webrtcAudioElement.setAttribute('webkit-playsinline', 'true');
      this.webrtcAudioElement.setAttribute('playsinline', 'true');
      this.webrtcAudioElement.setAttribute('x5-playsinline', 'true');
      this.webrtcAudioElement.setAttribute('x5-video-player-type', 'h5');
      
      this.webrtcAudioElement.style.display = 'none';
      document.body.appendChild(this.webrtcAudioElement);

      // 添加音频事件监听器用于调试
      this.webrtcAudioElement.addEventListener('loadstart', () => {
        console.log(`🔊 [${this.serviceName}] WebRTC 音频开始加载`);
      });
      this.webrtcAudioElement.addEventListener('canplay', () => {
        console.log(`🔊 [${this.serviceName}] WebRTC 音频可以播放`);
      });
      this.webrtcAudioElement.addEventListener('play', () => {
        console.log(`🔊 [${this.serviceName}] WebRTC 音频开始播放`);
      });
      this.webrtcAudioElement.addEventListener('error', (e) => {
        console.error(`🔊 [${this.serviceName}] WebRTC 音频播放错误:`, e);
      });
    }
  }

  /**
   * 显示音频播放提示（备用方案）
   * @private
   */
  private showAudioPlayPrompt(): void {
    const promptDiv = document.createElement('div');
    promptDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: rgba(0, 0, 0, 0.8);
      color: white;
      padding: 20px;
      border-radius: 8px;
      z-index: 10000;
      text-align: center;
      font-size: 16px;
    `;
    promptDiv.innerHTML = `
      <div>点击此处启用音频播放</div>
      <div style="font-size: 12px; margin-top: 8px; opacity: 0.8;">需要用户交互才能播放音频</div>
    `;

    const handleClick = async () => {
      try {
        if (this.webrtcAudioElement && this.webrtcAudioElement.srcObject) {
          await this.webrtcAudioElement.play();
          console.log(`🔊 [${this.serviceName}] 用户通过提示框交互，播放成功`);
        }
      } catch (error) {
        console.error(`🔊 [${this.serviceName}] 用户通过提示框交互，播放仍然失败:`, error);
      } finally {
        if (document.body.contains(promptDiv)) {
            document.body.removeChild(promptDiv);
        }
      }
    };

    promptDiv.addEventListener('click', handleClick);
    document.body.appendChild(promptDiv);

    setTimeout(() => {
      if (document.body.contains(promptDiv)) {
        document.body.removeChild(promptDiv);
      }
    }, 5000);
  }
  
  /**
   * 核心的音频解锁逻辑，使用临时音频元素
   * @private
   */
  private async performAudioUnlock(): Promise<void> {
    if (this.isAudioContextUnlocked) {
      return;
    }
    
    console.log(`🔊 [${this.serviceName}] 执行音频解锁操作...`);
    const unlockAudioElement = document.createElement('audio');
    unlockAudioElement.src = SILENT_AUDIO;
    unlockAudioElement.style.display = 'none';
    
    try {
      document.body.appendChild(unlockAudioElement);
      await unlockAudioElement.play();
      this.isAudioContextUnlocked = true;
      console.log(`✅ [${this.serviceName}] 音频上下文解锁成功!`);
    } catch (error) {
      console.error(`❌ [${this.serviceName}] 音频上下文解锁失败:`, error);
      this.isAudioContextUnlocked = false;
      throw error;
    } finally {
      unlockAudioElement.pause();
      unlockAudioElement.src = '';
      if (document.body.contains(unlockAudioElement)) {
        unlockAudioElement.remove();
      }
      console.log(`🔊 [${this.serviceName}] 临时解锁音频元素已移除。`);
    }
  }

  /**
   * 公共方法，必须在用户交互时调用。这是唯一需要从外部UI调用的方法。
   */
  public async userDidInteract(): Promise<void> {
    if (this.isAudioContextUnlocked || this.isUnlocking) {
      return;
    }

    this.isUnlocking = true;
    console.log(`[${this.serviceName}] 收到用户交互，准备解锁音频...`);

    const isWeChat = /MicroMessenger/i.test(navigator.userAgent);

    const unlockFn = () => this.performAudioUnlock().finally(() => { this.isUnlocking = false; });

    if (isWeChat) {
        console.log(`[${this.serviceName}] 在微信环境，等待 JS-SDK 或 Bridge 就绪...`);
        if (typeof (window as any).wx !== 'undefined' && (window as any).wx.ready) {
            (window as any).wx.ready(() => {
                console.log(`[${this.serviceName}] wx.ready 触发，执行解锁。`);
                unlockFn();
            });
        } else if (typeof (window as any).WeixinJSBridge !== 'undefined') {
             console.log(`[${this.serviceName}] WeixinJSBridge 已存在，直接执行解锁。`);
             unlockFn();
        } else {
            console.log(`[${this.serviceName}] 监听 WeixinJSBridgeReady 事件...`);
            const onBridgeReady = () => {
                document.removeEventListener('WeixinJSBridgeReady', onBridgeReady, false);
                unlockFn();
            };
            document.addEventListener('WeixinJSBridgeReady', onBridgeReady, false);
            setTimeout(() => {
                document.removeEventListener('WeixinJSBridgeReady', onBridgeReady, false);
                if (!this.isAudioContextUnlocked) {
                    console.warn(`[${this.serviceName}] WeixinJSBridgeReady 超时，解锁可能失败。`);
                    this.isUnlocking = false;
                }
            }, 3000);
        }
    } else {
        console.log(`[${this.serviceName}] 在普通浏览器环境，直接执行解锁。`);
        await unlockFn();
    }
  }
  
  /**
   * Service-specific session start logic for WebRTC
   */
  protected async startServiceSession(): Promise<void> {
    this.createWebRTCAudioElement();

    if (!this.isAudioContextUnlocked) {
      console.warn(`[${this.serviceName}] 警告：会话开始时音频上下文尚未解锁。播放可能会延迟或需要额外交互。`);
    }

    try {
      await this.initializeWebRTC();
      console.log(`[${this.serviceName} #${this.instanceId}] WebRTC 初始化完成。`);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Service-specific session stop logic for WebRTC
   */
  protected async stopServiceSession(): Promise<void> {
    if (this.dataChannel) {
      console.log(`📡 [${this.serviceName} #${this.instanceId}] Closing data channel`);
      this.cleanupDataChannelHandlers();
      this.dataChannel.close();
      this.dataChannel = null;
    }

    if (this.peerConnection) {
      console.log(`🔗 [${this.serviceName} #${this.instanceId}] Closing peer connection`);
      this.peerConnection.ontrack = null;
      this.peerConnection.close();
      this.peerConnection = null;
    }

    if (this.webrtcAudioElement) {
      console.log(`🔊 [${this.serviceName} #${this.instanceId}] Clearing audio stream`);
      this.webrtcAudioElement.srcObject = null;
      this.webrtcAudioElement.pause();
    }
  }

  /**
   * Service-specific destruction logic for WebRTC
   */
  protected async destroyService(): Promise<void> {
    if (this.webrtcAudioElement) {
      console.log(`🗑️ [${this.serviceName} #${this.instanceId}] Removing audio element from DOM`);
      this.webrtcAudioElement.remove();
      this.webrtcAudioElement = null;
    }
  }

  /**
   * Update the session instructions
   */
  async updateInstructions(): Promise<void> {
    if (!this.dataChannel || this.dataChannel.readyState !== 'open' || !this.config) {
      throw new Error('Data channel not open');
    }
    console.log('更新会话指令session.update', this.config.instructions);
    const event = {
      type: 'session.update',
      session: {
        voice: this.config.voice,
        instructions: this.config.instructions,
        temperature: 0.6,
        input_audio_noise_reduction: {
            type: "near_field",
        },
        input_audio_transcription: {
            model: "whisper-1",
            prompt: "Mandarin Chinese, Cantonese, Spanish, English, Russian, Arabic, Portuguese, Japanese, Indonesian, French, German, Korean, Turkish, Polish, Ukrainian, Dutch"
        },
        turn_detection: {
            type: "server_vad",
            threshold: 0.5,
            prefix_padding_ms: 300,
            silence_duration_ms: 500,
        }
      }
    };
    this.dataChannel.send(JSON.stringify(event));
  }
  
  /**
   * Initialize WebRTC connection to Azure OpenAI
   * @private
   */
  private async initializeWebRTC(): Promise<void> {
    try {
      if (!this.sessionInfo?.ephemeralKey || !this.sessionInfo?.webrtcUrl || !this.sessionInfo?.deployment || !this.config || !this.microphoneService) {
        console.log('初始化WebRTC失败，缺少参数');
        throw new Error('Cannot initialize WebRTC without session key');
      }

      const mediaStream = this.microphoneService.getMediaStream();
      if (!mediaStream) {
        console.log('初始化WebRTC失败，没有媒体流');
        throw new Error('No media stream available from microphone service');
      }

      this.peerConnection = new RTCPeerConnection();

      if (this.webrtcAudioElement) {
        this.peerConnection.ontrack = async (event) => {
          const audioElement = this.webrtcAudioElement;
          if (audioElement) {
            console.log('🔗 [WebRTC] 收到远程音频流', event.streams[0]);
            audioElement.srcObject = event.streams[0];

            console.log(`🔊 [${this.serviceName}] 尝试播放远程音频流...`);
            try {
              await audioElement.play();
              console.log(`✅ [${this.serviceName}] 远程音频流播放成功!`);
            } catch (playError) {
              console.error(`❌ [${this.serviceName}] 远程音频流播放失败:`, playError);
              this.showAudioPlayPrompt();
            }
          }
        };
      }

      const audioTracks = mediaStream.getAudioTracks();
      if (audioTracks.length > 0) {
        console.log('添加本地音频轨道', audioTracks[0]);
        this.peerConnection.addTrack(audioTracks[0], mediaStream);
      }

      this.dataChannel = this.peerConnection.createDataChannel('events');
      this.setupDataChannelHandlers();

      const offer = await this.peerConnection.createOffer();
      await this.peerConnection.setLocalDescription(offer);

      const answerSdp = await apiManager.sendWebRTCOffer(
        this.sessionInfo.webrtcUrl,
        this.sessionInfo.deployment,
        this.sessionInfo.ephemeralKey,
        offer.sdp || ''
      );

      const answer = new RTCSessionDescription({
        type: 'answer',
        sdp: answerSdp
      });
      
      await this.peerConnection.setRemoteDescription(answer);

    } catch (error) {
      this.handleError(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }
  
  /**
   * Set up data channel event handlers
   * @private
   */
  private setupDataChannelHandlers(): void {
    if (!this.dataChannel) {
        return;
    }

    this.dataChannel.onopen = async () => {
      console.log('📡 [DataChannel] OPEN: 数据通道打开');
      this.updateSessionConnectionStatus(SessionConnectionStatus.Connected);
      this.updateInstructions().catch(console.error);
      await this.microphoneService?.startListening();
    };

    this.dataChannel.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        console.log(`📡 [DataChannel] MESSAGE:`, data);
        this.logSessionEvent(data);
      } catch (error) {
        console.error('处理服务器消息时出错:', error);
        this.handleError(error instanceof Error ? error : new Error('Error processing message from server'));
      }
    };

    this.dataChannel.onclose = () => {
      console.log('📡 [DataChannel] CLOSE: 数据通道关闭');
      this.updateSessionConnectionStatus(SessionConnectionStatus.Disconnected);
    };

    this.dataChannel.onerror = (error) => {
        const errorMessage = error instanceof Event ? 'Generic Error' : JSON.stringify(error);
        console.error(`📡 [DataChannel] ERROR:`, errorMessage);
        this.handleError(new Error(`Data channel error: ${errorMessage}`));
    };
  }
  
  /**
   * Clean up data channel event handlers
   * @private
   */
  private cleanupDataChannelHandlers(): void {
    if (!this.dataChannel) {
        return;
    }

    this.dataChannel.onopen = null;
    this.dataChannel.onmessage = null;
    this.dataChannel.onclose = null;
    this.dataChannel.onerror = null;
  }
}